import { ActionReducerMap, MetaReducer } from '@ngrx/store';
import { routerReducer, RouterReducerState } from '@ngrx/router-store';

const APP_CONFIG = { enableRxDebug: false };
import { debug } from './meta-reducers/debug.reducer';
import { initStateFromLocalStorage } from './meta-reducers/init-state-from-local-storage.reducer';

import { RouterStateUrl } from './router/router.state';

import { micrositeSessionsReducer } from './coreAuth/coreAuth.reducer';
import { MicrositeSessionsState } from './coreAuth/coreAuth.models';

import { configurationReducer } from './configuration/configuration.reducer';
import { ConfigurationState } from './configuration/configuration.models';

import { micrositeTrackingReducer } from './coreTracking/coreTracking.reducer';
import { MicrositeTrackingState } from './coreTracking/coreTracking.models';

import { micrositeWebsocketsReducer } from './coreWebsockets/coreWebsockets.reducer';
import { MicrositeWebsocketsState } from './coreWebsockets/coreWebsockets.models';

import { micrositePoolsReducer } from './pool/pool.reducer';
import { MicrositePoolsState } from './pool/pool.models';
import { selectRouter } from './router/router.selector';
import { closeSecondaryMenu, previousRoute } from './router/router.actions';
import {
  selectConfiguration,
  selectConfigurationCore,
  selectConfigurationLocal,
} from './configuration/configuration.selectors';
import {
  callCoreSettings,
  failConfiguration,
  successCoreSettings,
} from './configuration/configuration.actions';

import { selectMicrositeWebsockets } from './coreWebsockets/coreWebsockets.selectors';
import { selectTracking } from './coreTracking/coreTracking.selectors';
import {
  clearPixel,
  replaceState,
  setPixel,
  track,
} from './coreTracking/coreTracking.actions';

import {
  selectMicrositeSessions,
  selectMicrositeSessionsIsLoggedIn,
  selectMicrositeSessionsLoading,
  selectMicrositeAuthToken,
} from './coreAuth/coreAuth.selectors';
import {
  callMicrositeRefreshTokens,
  callMicrositeSessions,
  failMicrositeSessions,
  loginCancelled,
  setCustomMultiplier,
  setMicrositeAuthTokenAuthorizationCode,
  setOptionalData,
  successMicrositeRefreshTokens,
  successMicrositeSessions,
  triggerRegistration,
  triggerSignIn,
} from './coreAuth/coreAuth.actions';

import { ManagersState } from './manager/manager.model';
import { managersReducer } from './manager/manager.reducer';
import {
  selectCurrentManager,
  selectLeaderboard,
  selectManagersList,
  selectMyManager,
  selectTopFollowed,
} from './manager/manager.selectors';

import { SyndicatesState } from './syndicates/syndicates.model';
import { syndicatesReducer } from './syndicates/syndicates.reducer';
import {
  selectFilters,
  selectList,
  selectSyndicate,
  selectWinList,
} from './syndicates/syndicates.selectors';

import {
  selectTicket,
  selectTicketFilters,
  selectTicketsList,
} from './tickets/tickets.selector';
import { ticketReducer } from './tickets/tickets.reducer';
import { TicketsState } from './tickets/tickets.model';
import { freeplayReducer } from './freeplay/freeplay.reducer';
import { FreeplayState } from './freeplay/freeplay.model';
import {
  freeplayListLoading,
  selectFreeplayList,
  selectFreeplayListError,
  selectFreeplayToken,
  selectFreeplayTokenError,
} from './freeplay/freeplay.selectors';

import { uiReducer } from './ui-states/ui.reducer';
import { UIState } from './ui-states/ui.model';
import { selectUIState } from './ui-states/ui.selectors';
import {
  clearMenuState,
  setMenuState,
  forceWalletLogout,
} from './ui-states/ui.actions';
import { SportEventState } from './sport-events/sport-events.model';
import { sportEventReducer } from './sport-events/sport-events.reducer';

export const reducers: ActionReducerMap<AppState> = {
  micrositePools: micrositePoolsReducer,

  micrositeWebsockets: micrositeWebsocketsReducer,
  micrositeTracking: micrositeTrackingReducer,
  micrositeSessions: micrositeSessionsReducer,

  router: routerReducer,
  configuration: configurationReducer,
  managers: managersReducer,
  syndicates: syndicatesReducer,
  tickets: ticketReducer,
  freeplay: freeplayReducer,
  ui: uiReducer,
  sport_events: sportEventReducer,
};

export const metaReducers: MetaReducer<AppState>[] = [
  initStateFromLocalStorage,
];

if (APP_CONFIG.enableRxDebug) {
  metaReducers.unshift(debug);
}

export interface AppState {
  micrositePools: MicrositePoolsState;

  micrositeWebsockets: MicrositeWebsocketsState;
  micrositeTracking: MicrositeTrackingState;
  micrositeSessions: MicrositeSessionsState;

  router: RouterReducerState<RouterStateUrl>;
  configuration: ConfigurationState;
  managers: ManagersState;
  syndicates: SyndicatesState;
  tickets: TicketsState;
  freeplay: FreeplayState;
  ui: UIState;
  sport_events: SportEventState;
}

export {
  selectMicrositeSessions,
  selectMicrositeSessionsLoading,
  selectMicrositeAuthToken,
  selectMicrositeSessionsIsLoggedIn,
  callMicrositeSessions,
  successMicrositeSessions,
  setOptionalData,
  setCustomMultiplier,
  callMicrositeRefreshTokens,
  successMicrositeRefreshTokens,
  setMicrositeAuthTokenAuthorizationCode,
  failMicrositeSessions,
  loginCancelled,
  triggerSignIn,
  triggerRegistration,
};

export { selectMicrositeWebsockets };

export { selectTracking, track, replaceState, setPixel, clearPixel };

export {
  selectConfiguration,
  selectConfigurationCore,
  selectConfigurationLocal,
  callCoreSettings,
  successCoreSettings,
  failConfiguration,
};

export { RouterStateUrl, selectRouter, previousRoute, closeSecondaryMenu };

export {
  selectManagersList,
  selectCurrentManager,
  selectMyManager,
  selectLeaderboard,
  selectTopFollowed,
};
export { selectSyndicate, selectFilters, selectList, selectWinList };

export { selectTicketsList, selectTicket, selectTicketFilters };

export {
  selectFreeplayList,
  selectFreeplayToken,
  selectFreeplayListError,
  selectFreeplayTokenError,
  freeplayListLoading,
};

export { selectUIState, setMenuState, clearMenuState, forceWalletLogout };
